import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { monthNames } from '../../i18n/i18n';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import ConfirmationModal from '../../components/modals/confirmation';
import OverlayLoader from '../../components/overlayLoader';
import Pages from '../../components/pages';
import { deletePayroll, getPaginatedPayrolls } from '../../services/payrolls';
import { rsdFormat, toLocalDateString } from '../../utils/helper';
import { onMutateSuccess, onQuerySuccess } from '../../utils/queryResponse';
import SetPayRollModal from './payrollSet';
import PayrollsFilter from './payrollsFilter';
import { SkeletonRows } from '../../components/skeleton';

/* ── page-scoped styles ── */
const pageStyles = `
  .sbr .bk{display:inline-flex;align-items:center;gap:3px;font-size:10.5px;font-weight:700;
    color:var(--muted);background:var(--surface);border:1px solid var(--line);border-radius:5px;
    padding:1px 6px;margin-left:5px;vertical-align:middle;line-height:1.4}
  .sbr .bk .ic{width:10px;height:10px;color:var(--faint)}

  .sbr .pyperiod{display:flex;align-items:center;gap:10px;background:var(--brand-50);border:1px solid var(--brand-100);
    border-radius:var(--r-pill);padding:6px 8px 6px 14px;font-size:13px;font-weight:700;color:var(--brand-ink)}
  .sbr .pyperiod .ic{width:15px;height:15px;color:var(--brand)}
  .sbr .pyperiod .nav{width:26px;height:26px;border-radius:8px;background:#fff;border:1px solid var(--brand-100);
    display:flex;align-items:center;justify-content:center;color:var(--brand-ink);cursor:pointer;flex-shrink:0}
  .sbr .pyperiod .nav svg{width:13px;height:13px}

  .sbr .kpi .barbg{height:6px;border-radius:999px;background:rgba(255,255,255,.18);margin-top:12px;overflow:hidden}
  .sbr .kpi .barbg>i{display:block;height:100%;border-radius:999px;background:#fff}
  .sbr .kpi .warnk{color:var(--warn)}

  .sbr .pyrun{background:var(--card);border:1px solid var(--line);border-radius:var(--r);box-shadow:var(--sh-2);overflow:hidden;position:relative;min-height:120px}
  .sbr .pyrun-top{display:flex;align-items:center;gap:11px;padding:16px 18px;border-bottom:1px solid var(--line)}
  .sbr .pyrun-top .ci{width:17px;height:17px;color:var(--brand)}
  .sbr .pyrun-top .t{font-size:13.5px;font-weight:800;color:var(--ink);letter-spacing:-.2px}
  .sbr .pyrun-top .meta{margin-left:auto;font-size:12px;font-weight:700;color:var(--muted);background:var(--surface);
    border:1px solid var(--line);border-radius:999px;padding:4px 12px}

  .sbr .pytbl{width:100%;border-collapse:collapse;font-size:13.5px}
  .sbr .pytbl thead th{text-align:left;font-weight:700;color:var(--muted);font-size:11px;text-transform:uppercase;
    letter-spacing:.5px;padding:13px 18px;border-bottom:1px solid var(--line);background:var(--surface)}
  .sbr .pytbl thead th.r{text-align:right}
  .sbr .pytbl tbody td{padding:13px 18px;border-bottom:1px solid var(--line-soft);color:var(--text);font-weight:500;vertical-align:middle}
  .sbr .pytbl tbody tr{transition:background .12s}
  .sbr .pytbl tbody tr:hover{background:var(--brand-50)}
  .sbr .pytbl .r{text-align:right}
  .sbr .pytbl .money{font-variant-numeric:tabular-nums;font-weight:800;color:var(--ink)}
  .sbr .pytbl .num{font-variant-numeric:tabular-nums}

  .sbr .emp{display:flex;align-items:center;gap:11px}
  .sbr .emp .a{width:34px;height:34px;border-radius:11px;background:var(--brand-50);border:1px solid var(--brand-100);
    color:var(--brand-ink);font-size:12px;font-weight:800;display:flex;align-items:center;justify-content:center;flex-shrink:0}
  .sbr .emp .a.g{background:linear-gradient(160deg,#34D399,#0EA5E9);border:0;color:#fff}
  .sbr .emp .a.v{background:linear-gradient(160deg,#A78BFA,#7C3AED);border:0;color:#fff}
  .sbr .emp .a.o{background:linear-gradient(160deg,#FBBF24,#D97706);border:0;color:#fff}
  .sbr .emp .nm{font-size:14px;font-weight:700;color:var(--ink);line-height:1.2}
  .sbr .emp .nm small{display:block;font-size:11.5px;font-weight:600;color:var(--faint);margin-top:1px}

  .sbr .rolechip{display:inline-flex;align-items:center;font-size:11.5px;font-weight:700;color:var(--muted);
    background:var(--surface);border:1px solid var(--line);border-radius:999px;padding:4px 11px}

  .sbr .pytbl tfoot td{padding:15px 18px;font-weight:800;color:var(--ink);background:var(--surface);border-top:2px solid var(--line)}
  .sbr .pytbl tfoot .lbl{font-size:11px;text-transform:uppercase;letter-spacing:.5px;color:var(--muted);font-weight:800}
  .sbr .pytbl tfoot .money{font-size:15px}

  .sbr .py-act-btn{background:none;border:none;padding:3px 5px;cursor:pointer;border-radius:6px;
    display:inline-flex;align-items:center;transition:background .15s}
  .sbr .py-act-btn:hover{background:var(--surface)}
`;

/* ── Icon helpers ── */
const RunIcon = () => (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="5 3 19 12 5 21 5 3"/>
    </svg>
);
const DownloadIcon = () => (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 15V3M7 10l5 5 5-5M5 21h14"/>
    </svg>
);
const CalIcon = () => (
    <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/>
        <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
);
const EditIcon = () => (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
);
const TrashIcon = () => (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6"/>
        <path d="M19 6l-1 14H6L5 6"/>
        <path d="M10 11v6M14 11v6"/>
        <path d="M9 6V4h6v2"/>
    </svg>
);

const initials = (name = '') => {
    const parts = name.trim().split(/\s+/)
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    return name.slice(0, 2).toUpperCase()
}

const AVATAR_VARIANTS = ['g', 'v', 'o']
const avatarVariant = (index) => AVATAR_VARIANTS[index % AVATAR_VARIANTS.length]

const PayrollsPage = () => {
    const { t } = useTranslation();

    const now = new Date()
    const [periodYear, setPeriodYear] = useState(now.getFullYear())
    const [periodMonth, setPeriodMonth] = useState(now.getMonth())
    const [confirmationModal, setConfirmationModal] = useState(false)
    const [setPayrollModal, setSetPayrollModal] = useState(false)
    const [payrolls, setPayrolls] = useState([])
    const [selectedPayroll, setSelectedPayroll] = useState()
    const [pagination, setPagination] = useState()
    const [pageNumber, setPageNumber] = useState(1)
    const [pageSize, setPageSize] = useState(10)
    const [filters, setFilters] = useState(['', '', '', ''])

    const { data, isFetching } = useQuery(
        ['payrolls', 'paginated', pageNumber, pageSize, ...filters],
        () => getPaginatedPayrolls(pageNumber, pageSize, ...filters)
    )

    const queryClient = useQueryClient()
    const { mutate, isLoading: isDeleting } = useMutation(deletePayroll, {
        onSuccess: onMutateSuccess(() => {
            if (payrolls.length === 1 && pageNumber > 1) setPageNumber(pageNumber - 1)
            queryClient.invalidateQueries(['payrolls', 'paginated'])
        })
    })

    useEffect(() => {
        onQuerySuccess(data, (result) => {
            setPagination(result)
            setPayrolls(result.data.map(x => ({
                ...x,
                amount:      parseFloat(x.amount),
                bonus:       parseFloat(x.bonus),
                deduction:   parseFloat(x.deduction),
                totalSalary: parseFloat(x.totalSalary),
            })))
        })
    }, [data])

    const handleSetPayrollModal   = (payroll) => { setSelectedPayroll(payroll); setSetPayrollModal(true) }
    const handleConfirmationModal = (payroll) => { setSelectedPayroll(payroll); setConfirmationModal(true) }
    const handleDeletePayroll     = () => mutate(selectedPayroll.id)
    const handleSetPageSize       = (size) => { setPageNumber(1); setPageSize(size) }

    const prevPeriod = () => {
        if (periodMonth === 0) { setPeriodMonth(11); setPeriodYear(y => y - 1) }
        else setPeriodMonth(m => m - 1)
    }
    const nextPeriod = () => {
        if (periodMonth === 11) { setPeriodMonth(0); setPeriodYear(y => y + 1) }
        else setPeriodMonth(m => m + 1)
    }

    const periodLabel = `${monthNames()[periodMonth]} ${periodYear}`

    const totalGross      = payrolls.reduce((s, p) => s + (p.totalSalary || p.amount || 0), 0)
    const avgPay          = payrolls.length > 0 ? totalGross / payrolls.length : 0
    const totalDeductions = payrolls.reduce((s, p) => s + (p.deduction || 0), 0)
    const totalGrossTarget = totalGross > 0 ? totalGross : 1
    const barWidth     = Math.min(100, (totalGross / (totalGrossTarget * 1.3)) * 100).toFixed(1)

    return (
        <div className="sbr">
            <style>{pageStyles}</style>

            <ConfirmationModal display={confirmationModal} toggle={setConfirmationModal} confirmAction={handleDeletePayroll}
                title={t('common.delete')} message={t('common.confirm')} />
            <SetPayRollModal display={setPayrollModal} toggle={setSetPayrollModal} payrollId={selectedPayroll?.id} />

            {/* ── Desktop page header ── */}
            <div className="dhead sbr-d">
                <div>
                    <div className="ttl">{t('payroll.title')}</div>
                    <div className="sub">{payrolls.length === 1 ? t('payroll.payCycleForOne', { period: periodLabel }) : t('payroll.payCycleFor', { period: periodLabel, count: payrolls.length })}</div>
                </div>
                <div className="act">
                    <div className="pyperiod">
                        <span className="nav" onClick={prevPeriod}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                                <polyline points="15 18 9 12 15 6"/>
                            </svg>
                        </span>
                        <CalIcon />
                        {periodLabel}
                        <span className="nav" onClick={nextPeriod}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                                <polyline points="9 18 15 12 9 6"/>
                            </svg>
                        </span>
                    </div>
                    <button className="btn-sbr btn-ghost-sbr btn-sm-sbr">
                        <DownloadIcon /> {t('common.export')}
                    </button>
                    <button className="btn-sbr btn-primary-sbr btn-sm-sbr" onClick={() => handleSetPayrollModal()}>
                        <RunIcon /> {t('payroll.runPayroll')}
                    </button>
                </div>
            </div>

            {/* ── Mobile appbar ── */}
            <div className="sbr-m" style={{ marginBottom:'16px' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                    <div style={{ flex:1, fontSize:'18px', fontWeight:800, color:'var(--ink)', letterSpacing:'-.4px' }}>{t('payroll.title')}</div>
                    <button className="btn-sbr btn-primary-sbr btn-sm-sbr" onClick={() => handleSetPayrollModal()}>
                        + {t('common.add')}
                    </button>
                </div>
            </div>


{/* ── Desktop KPIs ── */}
            <div className="kpis4 sbr-d">
                <div className="kpi lift">
                    <div className="l">
                        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <line x1="12" y1="1" x2="12" y2="23"/>
                            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                        </svg>
                        {t('payroll.totalPayrollKpi')}
                    </div>
                    <div className="n">{rsdFormat(totalGross)} <span style={{ fontSize:'14px', fontWeight:700, opacity:.7 }}>RSD</span></div>
                    <div className="barbg"><i style={{ width: `${barWidth}%` }}></i></div>
                </div>
                <div className="kpi">
                    <div className="l">
                        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                            <circle cx="9" cy="7" r="4"/>
                            <path d="M23 21v-2a4 4 0 0 0-3-3.9"/>
                        </svg>
                        {t('payroll.employees')}
                    </div>
                    <div className="n">{payrolls.length}</div>
                    <div className="d flat">{t('payroll.recordsThisPeriod')}</div>
                </div>
                <div className="kpi">
                    <div className="l">
                        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <line x1="12" y1="20" x2="12" y2="10"/>
                            <line x1="18" y1="20" x2="18" y2="4"/>
                            <line x1="6" y1="20" x2="6" y2="16"/>
                        </svg>
                        {t('payroll.avgPay')}
                    </div>
                    <div className="n">{rsdFormat(avgPay)} <span style={{ fontSize:'13px', fontWeight:700, color:'var(--muted)' }}>RSD</span></div>
                    <div className="d flat">{t('payroll.perEmployee')}</div>
                </div>
                <div className="kpi">
                    <div className="l">
                        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <circle cx="12" cy="12" r="10"/><line x1="8" y1="12" x2="16" y2="12"/>
                        </svg>
                        {t('payroll.deductions')}
                    </div>
                    <div className="n">{rsdFormat(totalDeductions)} <span style={{ fontSize:'13px', fontWeight:700, color:'var(--muted)' }}>RSD</span></div>
                    <div className="d flat">{t('payroll.withheldThisPeriod')}</div>
                </div>
            </div>

            {/* ── Filters ── */}
            <div style={{ marginBottom: '18px' }}>
                <PayrollsFilter onFilter={setFilters} />
            </div>

            {/* ── Payroll run table ── */}
            <div className="pyrun">
                <div className="pyrun-top">
                    <svg className="ci" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M9 12l2 2 4-4"/>
                        <path d="M21 12c0 5-3.5 7.5-8.66 9a1 1 0 0 1-.67 0C6.5 19.5 3 17 3 12V5a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C13.5 2.8 16 4 18 4a1 1 0 0 1 1 1z"/>
                    </svg>
                    <span className="t">{t('payroll.periodRun', { period: periodLabel })}</span>
                    {pagination?.totalCount > 0 && (
                        <span className="meta">{pagination.totalCount} total</span>
                    )}
                </div>

                {/* Page-size selector */}
                <div style={{ display:'flex', alignItems:'center', justifyContent:'flex-end', padding:'10px 18px 0', gap:'10px' }}>
                    <select style={{ fontSize:'12px', border:'1px solid var(--line)', borderRadius:'8px', padding:'5px 10px', background:'var(--surface)', color:'var(--text)' }}
                        onChange={(e) => handleSetPageSize(e.target.value)} defaultValue="10">
                        <option value="10">{t('payroll.perPage', { size: 10 })}</option>
                        <option value="20">{t('payroll.perPage', { size: 20 })}</option>
                        <option value="50">{t('payroll.perPage', { size: 50 })}</option>
                        <option value="100">{t('payroll.perPage', { size: 100 })}</option>
                    </select>
                </div>

                {payrolls.length > 0 ? (
                    <table className="pytbl">
                        <thead>
                            <tr>
                                <th>{t('payroll.employee')}</th>
                                <th>{t('payroll.rate')}</th>
                                <th className="r">{t('payroll.bonus')}</th>
                                <th className="r">{t('payroll.deductions')}</th>
                                <th className="r">{t('payroll.grossPay')}</th>
                                <th className="r"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {payrolls.map((payroll, idx) => {
                                // The paginated endpoint returns `employee` as a pre-joined
                                // "First Last" string (see payrollGet controller). Fall back to
                                // object shapes in case other endpoints return the relation.
                                const employeeName =
                                    (typeof payroll.employee === 'string' && payroll.employee.trim())
                                        ? payroll.employee.trim()
                                        : payroll.employee?.user
                                            ? `${payroll.employee.user.firstName ?? ''} ${payroll.employee.user.lastName ?? ''}`.trim()
                                            : payroll.employee?.firstName
                                                ? `${payroll.employee.firstName ?? ''} ${payroll.employee.lastName ?? ''}`.trim()
                                                : ''
                                const displayName = employeeName || t('payroll.unassigned')
                                const avatarLetters = employeeName ? initials(employeeName) : '—'
                                const variant = avatarVariant(idx)
                                const RATE_TYPE_MAP = {
                                    HOURLY:        { labelKey: 'payroll.hourly',  unitKey: 'payroll.perHour'  },
                                    DAILY:         { labelKey: 'payroll.daily',   unitKey: 'payroll.perDay'   },
                                    FIXED_MONTHLY: { labelKey: 'payroll.monthly', unitKey: 'payroll.perMonth' },
                                }
                                const rt = RATE_TYPE_MAP[payroll.rateType]
                                return (
                                    <tr key={payroll.id}>
                                        <td>
                                            <div className="emp">
                                                <div className={`a ${variant}`}>{avatarLetters}</div>
                                                <div className="nm">
                                                    {displayName}
                                                    {payroll.payStartDate && (
                                                        <small>{toLocalDateString(payroll.payStartDate, 'dd.MM.yyyy')}{payroll.payEndDate ? ` – ${toLocalDateString(payroll.payEndDate, 'dd.MM.yyyy')}` : ''}</small>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            {(rt || payroll.rateType) ? (
                                                <div style={{ display:'flex', flexDirection:'column', gap:'3px' }}>
                                                    <span className="rolechip" style={{ alignSelf:'flex-start' }}>{rt ? t(rt.labelKey) : payroll.rateType}</span>
                                                    {payroll.rate != null && payroll.rate !== '' && (
                                                        <span className="num" style={{ fontSize:'12px', color:'var(--muted)', fontWeight:600 }}>
                                                            {rsdFormat(payroll.rate)}{rt ? t(rt.unitKey) : ''}
                                                        </span>
                                                    )}
                                                </div>
                                            ) : <span style={{ color:'var(--faint)', fontSize:'12px' }}>—</span>}
                                        </td>
                                        <td className="r num" style={{ color: payroll.bonus > 0 ? 'var(--good)' : 'var(--faint)', fontWeight: payroll.bonus > 0 ? 700 : 500 }}>
                                            {payroll.bonus > 0 ? `+${rsdFormat(payroll.bonus)}` : '—'}
                                        </td>
                                        <td className="r num" style={{ color: payroll.deduction > 0 ? 'var(--bad)' : 'var(--faint)', fontWeight: payroll.deduction > 0 ? 700 : 500 }}>
                                            {payroll.deduction > 0 ? `−${rsdFormat(payroll.deduction)}` : '—'}
                                        </td>
                                        <td className="r money">{rsdFormat(payroll.totalSalary ?? payroll.amount)}</td>
                                        <td className="r">
                                            <span style={{ display:'flex', gap:'4px', justifyContent:'flex-end' }}>
                                                <button title={t('common.edit')} className="py-act-btn" style={{ color:'var(--brand)' }}
                                                    onClick={() => handleSetPayrollModal(payroll)}>
                                                    <EditIcon />
                                                </button>
                                                <button title={t('common.delete')} className="py-act-btn" style={{ color:'#94A3B8' }}
                                                    onClick={() => handleConfirmationModal(payroll)}>
                                                    <TrashIcon />
                                                </button>
                                            </span>
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colSpan={4}><span className="lbl">{payrolls.length === 1 ? t('payroll.totalRecordOne') : t('payroll.totalRecords', { count: payrolls.length })}</span></td>
                                <td className="r money">{rsdFormat(totalGross)}</td>
                                <td></td>
                            </tr>
                        </tfoot>
                    </table>
                ) : (
                    isFetching ? (
                        <div style={{ padding: '16px 18px' }}>
                            <SkeletonRows rows={6} widths={['26%', '16%', '14%', '14%', '16%', '8%']} />
                        </div>
                    ) : (
                        <div style={{ padding: '48px 0', textAlign: 'center', color: 'var(--muted)', fontStyle: 'italic', fontSize: '14px' }}>
                            {t('payroll.noPayrollRecords')}
                        </div>
                    )
                )}

                {((isFetching && payrolls.length > 0) || isDeleting) && <OverlayLoader />}
            </div>

            {/* ── Pagination ── */}
            {pagination?.totalPages > 0 && (
                <div style={{ marginTop: '16px' }}>
                    <Pages paginationData={pagination} changePage={setPageNumber} pageNumber={pageNumber} />
                </div>
            )}

            <div className="spacer"></div>
        </div>
    )
}

export default PayrollsPage;
